import { Link } from "react-router-dom";
import { Instagram, Youtube, Facebook, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { SITE } from "@/data/site";
import { FOOTER } from "@/constants/testIds";

const socials = [
  { name: "instagram", icon: Instagram, href: SITE.socials.instagram },
  { name: "youtube", icon: Youtube, href: SITE.socials.youtube },
  { name: "facebook", icon: Facebook, href: SITE.socials.facebook },
];

const quickLinks = [
  { label: "Home", to: "/" },
  { label: "Contact", to: "/contact" },
  { label: "Work From Home", to: "/#work-from-home" },
  { label: "Topics", to: "/#topics" },
];

export default function Footer() {
  return (
    <footer
      data-testid={FOOTER.section}
      className="relative overflow-hidden bg-black text-white"
    >
      <div className="ts-grid-bg pointer-events-none absolute inset-0 opacity-[0.04]" />
      <div className="relative mx-auto max-w-6xl px-5 py-20 sm:px-8">
        <div className="grid gap-14 lg:grid-cols-[1.4fr_1fr_1fr]">
          <div>
            <p className="font-display text-xs font-semibold uppercase tracking-[0.3em] text-cyan-accent">
              Let's build together
            </p>
            <h2 className="mt-4 font-display text-4xl font-black leading-[0.95] tracking-tight sm:text-6xl">
              Let's <span className="text-brand-light">talk.</span>
            </h2>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-neutral-500">
              Have a collaboration, project or business inquiry? Reach out and
              let's create something ahead of the curve.
            </p>
            <Link
              to="/contact"
              className="group mt-7 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-black transition-transform duration-200 hover:-translate-y-0.5 hover:bg-brand-hover"
            >
              Get in touch
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          <div>
            <h3 className="font-display text-sm font-semibold uppercase tracking-widest text-neutral-400">
              Quick Links
            </h3>
            <ul className="mt-5 space-y-3">
              {quickLinks.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    className="text-sm text-neutral-400 transition-colors hover:text-white"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-display text-sm font-semibold uppercase tracking-widest text-neutral-400">
              Reach us
            </h3>
            <ul className="mt-5 space-y-3 text-sm text-neutral-400">
              <li className="flex items-center gap-2.5">
                <Mail className="h-4 w-4 text-brand-light" /> {SITE.email}
              </li>
              <li className="flex items-center gap-2.5">
                <MapPin className="h-4 w-4 text-brand-light" /> {SITE.location}
              </li>
            </ul>
            <div className="mt-6 flex gap-3">
              {socials.map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={FOOTER.social(s.name)}
                  aria-label={s.name}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-neutral-400 transition-[transform,background-color,color] duration-200 hover:-translate-y-0.5 hover:bg-brand hover:text-black"
                >
                  <s.icon className="h-4.5 w-4.5" strokeWidth={1.8} />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row sm:items-center">
          <Logo dark testId="footer-logo" />
          <p className="text-xs text-neutral-400">
            © {new Date().getFullYear()} Tech Siksha. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
